import BlackBackIcon from 'assets/svgs/BlackBackIcon';
import CameraIcon from 'assets/svgs/CameraIcon';
import DefaultAvatarIcon from 'assets/svgs/DefaultAvatarIcon';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/interfaces';
import { apiEditProfile, apiEditProfileImage } from 'shared/api/user';
import { LoadingCircular } from 'shared/ui-kit/LoadingCircular';
import { PrimaryButton } from 'shared/ui-kit/PrimaryButton/PrimaryButton';
import { getBase64String } from 'shared/utils-IPFS/functions';
import useIPFS from 'shared/utils-IPFS/useIPFS';
import profileAction from 'redux/profile/actions';

import './index.styles.scss';
import { showToast } from 'shared/ui-kit/PrimaryToast';

export const MobileEditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state: RootState) => state.profile);
  const { uploadBase64 } = useIPFS();

  const hiddenFileInput = useRef<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const [imageUrl, setImageUrl] = useState('');
  const [userName, setUserName] = useState('');
  const [description, setDescription] = useState('');

  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (userInfo) {
      setImageUrl(userInfo.image);
      setUserName(userInfo.name);
      setDescription(userInfo.description);
    }
  }, [userInfo]);

  useEffect(() => {
    onInput(null);
  }, [description]);

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const handleChange = async (event) => {
    event.preventDefault();
    const files = event.target.files;
    if (files.length) {
      const name = files[0].name;
      const size = files[0].size;
      const type = files[0].type;
      setIsUploadingFile(true);

      const base64String = await getBase64String(files[0]);
      const url = await uploadBase64(base64String, type, '');

      const res = await apiEditProfileImage({ profileImage: url });
      if (res.success) {
        dispatch(profileAction.loadUserInfo(userInfo?.id ?? 0));
        setImageUrl(url + '');
      }

      setIsUploadingFile(false);
    }
  };

  const onUpdateProfile = async () => {
    if (!isUpdating && !!userName) {
      setIsUpdating(true);

      const res = await apiEditProfile({
        username: userName,
        description: description
      });

      if (res.success) {
        dispatch(profileAction.loadUserInfo(userInfo?.id ?? 0));

        showToast('success', 'Successfully updated');
        navigate(-1);
      }

      setIsUpdating(false);
    }
  };

  const onInput = (e) => {
    if (inputRef.current) {
      if (description !== '') {
        inputRef.current.style.height = '1px';
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
      } else {
        inputRef.current.style.height = '40px';
      }
    }
  };

  const renderHeader = () => {
    return (
      <div className="edit_profile_header row_align_items row_space_between">
        <div
          onClick={() => {
            navigate(-1);
          }}
        >
          <BlackBackIcon />
        </div>
        <div className="font-bold font16">Edit Profile</div>
        <div style={{ marginRight: 24 }} />
      </div>
    );
  };

  const renderBody = () => {
    return (
      <div className="edit_profile_body">
        {renderImageSection()}
        {renderInputSection()}
      </div>
    );
  };

  const renderImageSection = () => {
    return (
      <div className="image_section">
        {userInfo !== undefined && userInfo?.image !== '' ? (
          <img
            src={imageUrl}
            style={{
              width: 100,
              height: 100,
              borderRadius: 100,
              objectFit: 'cover'
            }}
          />
        ) : (
          <DefaultAvatarIcon size={100} />
        )}
        <div className="camera_icon_tag clickable" onClick={handleClick}>
          {isUploadingFile ? (
            <LoadingCircular color="white" size={20} />
          ) : (
            <CameraIcon size={20} />
          )}
        </div>

        <input
          type="file"
          ref={hiddenFileInput}
          onChange={handleChange}
          style={{ display: 'none' }}
          accept="image/*"
        />
      </div>
    );
  };

  const renderInputSection = () => {
    return (
      <div className="input_sections">
        <div className="input_section">
          <div className="sub_title font-regular font10">Display Name</div>
          <input
            value={userName}
            placeholder="Enter your name here..."
            className="sub_input font-regular font16"
            onChange={(e) => setUserName(e.target.value)}
          />
        </div>
        <div className="input_section">
          <div className="sub_title font-regular font10">Short Bio</div>
          <textarea
            ref={inputRef}
            value={description}
            placeholder={'Enter your bio here...'}
            className="sub_input font-regular font16"
            onInput={onInput}
            onChange={(e) => {
              if (e.nativeEvent['inputType'] === 'insertLineBreak') {
                e.preventDefault();
                return;
              } else {
                setDescription(e.target.value);
              }
            }}
          />
        </div>
      </div>
    );
  };

  const renderBottom = () => {
    return (
      <div className="edit_profile_bottom">
        <PrimaryButton
          label="Update Profile"
          isLoading={isUpdating}
          onPressed={() => {
            onUpdateProfile();
          }}
        />
      </div>
    );
  };

  return (
    <div className="mobile_edit_profile">
      {renderHeader()}
      {renderBody()}
      {renderBottom()}
    </div>
  );
};
